import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

function extractError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    return first?.msg || first?.message || 'Invalid request';
  }
  return 'Something went wrong.';
}

export default function AdminRegisterPage() {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '', confirm_password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirm_password) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/admin/register', {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        password: form.password,
      });
      setSubmitted(true);
    } catch (err) {
      setError(extractError(err));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--paper)', padding: 20 }}>
        <div style={{ width: '100%', maxWidth: 460, textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--forest)', color: 'var(--paper)', display: 'grid', placeItems: 'center', fontSize: 28, margin: '0 auto 24px' }}>✓</div>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 24, fontWeight: 700, color: 'var(--forest)', marginBottom: 12 }}>
            Request submitted
          </div>
          <p style={{ fontSize: 15, color: 'var(--ink-3)', lineHeight: 1.7, marginBottom: 28 }}>
            If <strong>{form.email}</strong> is eligible, we've sent a verification link to that
            address. Click the link in the email to confirm ownership — your request will then go
            to the PropAtlas team for review.
          </p>
          <Link to="/login" style={{ color: 'var(--forest)', fontSize: 14, textDecoration: 'none', fontWeight: 500 }}>
            ← Back to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--paper)', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 460 }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <img src="/brand/svg/propatlas-mark.svg" className="brand-mark" alt="PropAtlas mark" style={{ width: 34 }} />
            <span className="brand-name" style={{ fontSize: 28 }}>Prop<em>A</em>tlas</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Request admin access</div>
        </div>

        <div className="panel" style={{ padding: 32 }}>
          {/* Notice banner */}
          <div style={{ background: '#fff8e1', border: '1px solid #f5c842', borderRadius: 'var(--r-sm)', padding: '12px 16px', marginBottom: 24, fontSize: 13, color: '#7a5c00', lineHeight: 1.6 }}>
            <strong>Approval required.</strong> Admin accounts must be reviewed and approved by the PropAtlas team before you can log in.
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {error && (
              <div style={{ padding: 10, background: 'rgba(139,46,46,0.08)', border: '1px solid rgba(139,46,46,0.2)', borderRadius: 4, color: 'var(--crimson)', fontSize: 13 }}>
                {error}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="field">
                <label>First name</label>
                <input
                  type="text" required autoComplete="given-name"
                  value={form.first_name} onChange={set('first_name')}
                  placeholder="Jane"
                />
              </div>
              <div className="field">
                <label>Last name</label>
                <input
                  type="text" required autoComplete="family-name"
                  value={form.last_name} onChange={set('last_name')}
                  placeholder="Smith"
                />
              </div>
            </div>

            <div className="field">
              <label>Email address</label>
              <input
                type="email" required autoComplete="email"
                value={form.email} onChange={set('email')}
                placeholder="admin@example.com"
              />
            </div>

            <div className="field">
              <label>Password</label>
              <input
                type="password" required autoComplete="new-password"
                value={form.password} onChange={set('password')}
                placeholder="Min. 8 chars, upper + lower + digit"
              />
            </div>

            <div className="field">
              <label>Confirm password</label>
              <input
                type="password" required autoComplete="new-password"
                value={form.confirm_password} onChange={set('confirm_password')}
                placeholder="Re-enter password"
              />
            </div>

            <button
              type="submit"
              className="btn primary"
              disabled={loading}
              style={{ height: 44, fontSize: 14, marginTop: 4 }}
            >
              {loading ? 'Submitting…' : 'Submit request'}
            </button>
          </form>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--ink-4)' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--forest)', fontWeight: 500 }}>Sign in</Link>
        </div>
      </div>
    </div>
  );
}
